import { useState } from "react";
import type { NetWorthSnapshot } from "../../lib/api-modules/types.js";
import { ACCOUNT_TYPE_LABELS } from "../../lib/api-modules/types.js";
import { formatCentsCompact } from "../../lib/util/money.js";

/**
 * Three-tab breakdown (By account / By type / By owner) over the latest
 * snapshot's `by_*` maps. Renders one row per group with a horizontal
 * proportional bar sized to the absolute value (so a mortgage liability
 * has visual weight even though it's signed negative).
 *
 * Plain CSS bars rather than a Recharts BarChart: the row labels +
 * tabular-numbers values are the primary content, and the bar is just a
 * proportional indicator. A chart-library version would be ceremony.
 */

type Tab = "account" | "type" | "owner";

interface BreakdownProps {
  readonly snapshot: NetWorthSnapshot;
  readonly hideValues: boolean;
}

export function Breakdown({ snapshot, hideValues }: BreakdownProps) {
  const [tab, setTab] = useState<Tab>("type");

  const rows = buildRows(snapshot, tab);
  const maxAbs = rows.reduce(
    (m, r) => Math.max(m, Math.abs(r.value_cents)),
    1,
  );

  return (
    <div data-testid="networth-breakdown">
      <div className="mb-4 flex gap-2 text-sm">
        <TabButton
          active={tab === "account"}
          onClick={() => setTab("account")}
        >
          By account
        </TabButton>
        <TabButton active={tab === "type"} onClick={() => setTab("type")}>
          By type
        </TabButton>
        <TabButton active={tab === "owner"} onClick={() => setTab("owner")}>
          By owner
        </TabButton>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-[var(--color-muted)]">
          No values to break down yet.
        </p>
      ) : (
        <ul className="space-y-2.5" data-testid="breakdown-rows">
          {rows.map((r) => {
            const widthPct = (Math.abs(r.value_cents) / maxAbs) * 100;
            const isNegative = r.value_cents < 0;
            return (
              <li key={r.label} data-testid="breakdown-row">
                <div className="flex items-center justify-between text-sm">
                  <span className="truncate text-[var(--color-text)]">{r.label}</span>
                  <span
                    className={
                      isNegative
                        ? "font-mono tabular-nums font-medium text-[var(--color-danger)]"
                        : "font-mono tabular-nums font-medium text-[var(--color-text)]"
                    }
                  >
                    {formatCentsCompact(r.value_cents, { hidden: hideValues })}
                  </span>
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-[var(--color-elevated)]">
                  <div
                    className={
                      isNegative
                        ? "h-1.5 rounded-full bg-[var(--color-danger)]"
                        : "h-1.5 rounded-full bg-[var(--color-accent)]"
                    }
                    style={{ width: `${widthPct.toFixed(2)}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? "rounded-md bg-[var(--color-accent-soft)] px-3 py-1 text-sm font-medium text-[var(--color-accent)]"
          : "rounded-md px-3 py-1 text-sm text-[var(--color-muted)] hover:bg-[var(--color-elevated)] hover:text-[var(--color-text)]"
      }
    >
      {children}
    </button>
  );
}

function buildRows(
  snapshot: NetWorthSnapshot,
  tab: Tab,
): Array<{ label: string; value_cents: number }> {
  if (tab === "account") {
    return snapshot.by_account.map((a) => ({
      label: a.name,
      value_cents: a.value_cents,
    }));
  }
  if (tab === "type") {
    return Object.entries(snapshot.by_account_type)
      .map(([k, v]) => ({
        label: ACCOUNT_TYPE_LABELS[k as keyof typeof ACCOUNT_TYPE_LABELS] ?? k,
        value_cents: v,
      }))
      .sort((a, b) => Math.abs(b.value_cents) - Math.abs(a.value_cents));
  }
  // owner
  return Object.entries(snapshot.by_owner)
    .map(([k, v]) => ({
      label: ownerLabel(k),
      value_cents: v,
    }))
    .sort((a, b) => Math.abs(b.value_cents) - Math.abs(a.value_cents));
}

function ownerLabel(owner: string): string {
  switch (owner) {
    case "primary":
      return "Primary";
    case "spouse":
      return "Spouse";
    case "joint":
      return "Joint";
    case "minor":
      return "Minor";
    case "trust":
      return "Trust";
    default:
      return owner;
  }
}
